import { useEffect, useState } from "react";

/**
 * The one width the product changes shape at: Tailwind's `lg`, which is also where AskMila's own
 * side panel stops being an overlay.
 *
 * Every responsive rule in the shell and in the agent screens is written against this one number,
 * in CSS as `lg:` and here in JavaScript for the handful of decisions CSS cannot make — how tall
 * an hour is on the day's clock, and which size of control a conversation's card carries. Two
 * breakpoints would mean a window width at which the layout has stacked but the cards have not.
 */
export const DESKTOP_QUERY = "(min-width: 992px)";

const mediaQuery = () =>
  typeof window !== "undefined" && typeof window.matchMedia === "function"
    ? window.matchMedia(DESKTOP_QUERY)
    : null;

/**
 * Whether the viewport is at or past `lg`.
 *
 * <b>Desktop is the answer when there is no `matchMedia` to ask</b> — server rendering, and jsdom,
 * which has none. A component that assumed mobile there would render its narrow shape in every
 * test that never said otherwise.
 *
 * `addListener` is kept as a fallback: `MediaQueryList` only grew `addEventListener` in Safari 14,
 * and a rail that never hears the window change is a rail that is drawn for the wrong width until
 * the next reload.
 */
export const useIsDesktop = () => {
  const [isDesktop, setIsDesktop] = useState(() => mediaQuery()?.matches ?? true);

  useEffect(() => {
    const query = mediaQuery();
    if (!query) return undefined;

    const onChange = (event) => setIsDesktop(event.matches);

    // Read once more on mount: the first paint answered from the query as it was at render, and
    // the window can have changed between the two.
    setIsDesktop(query.matches);

    if (query.addEventListener) {
      query.addEventListener("change", onChange);
      return () => query.removeEventListener("change", onChange);
    }

    query.addListener(onChange);
    return () => query.removeListener(onChange);
  }, []);

  return isDesktop;
};

export default useIsDesktop;
